import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import { ChevronDown, ChevronRight, Trash2 } from "lucide-react";
import { trpc, type RouterOutputs } from "@/hooks/trpc";
import { toast } from "sonner";
import type { FoodEquipmentParameterId } from "@brewbase/db/types";
type EquipmentWithParams = RouterOutputs["food"]["equipment"]["listWithParams"][number];
type EquipmentParam = RouterOutputs["food"]["equipment"]["listParameters"][number];

interface EquipmentCardProps {
  equipment: EquipmentWithParams;
  allParameters: EquipmentParam[];
  onDelete: () => void;
  isDeleting: boolean;
}

export function EquipmentCard({
  equipment,
  allParameters,
  onDelete,
  isDeleting,
}: EquipmentCardProps) {
  const utils = trpc.useUtils();
  const [expanded, setExpanded] = useState(false);

  const setParamsMutation = trpc.food.equipment.setParams.useMutation({
    onSuccess: () => utils.food.equipment.listWithParams.invalidate(),
    onError: (e) => toast.error(e.message),
  });

  const assignedIds = useMemo(
    () => new Set(equipment.parameters.map((p) => p.id)),
    [equipment.parameters],
  );

  const availableParams = useMemo<ComboboxOption[]>(
    () =>
      allParameters
        .filter((p) => !assignedIds.has(p.id))
        .map((p) => ({
          value: String(p.id),
          label: p.unit ? `${p.name} (${p.unit})` : p.name,
        })),
    [allParameters, assignedIds],
  );

  const handleAddParam = (paramId: string) => {
    const nextIds = [...equipment.parameters.map((p) => p.id), Number(paramId) as FoodEquipmentParameterId];
    setParamsMutation.mutate({ equipment_id: equipment.id, parameter_ids: nextIds });
  };

  const handleRemoveParam = (paramId: number) => {
    const nextIds = equipment.parameters.filter((p) => p.id !== paramId).map((p) => p.id);
    setParamsMutation.mutate({ equipment_id: equipment.id, parameter_ids: nextIds });
  };

  return (
    <div className="rounded-md border">
      <div className="flex items-center gap-2 px-3 py-2 hover:bg-muted/40">
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1.5 flex-1 min-w-0 text-left cursor-pointer"
        >
          {expanded ? (
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          )}
          <span className="text-sm font-medium truncate">{equipment.name}</span>
          <span className="text-xs text-muted-foreground shrink-0">
            ({equipment.parameters.length} parameter
            {equipment.parameters.length !== 1 ? "s" : ""})
          </span>
        </button>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-muted-foreground hover:text-destructive shrink-0"
          disabled={isDeleting}
          onClick={onDelete}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>

      {expanded && (
        <div className="border-t px-3 py-3 space-y-2">
          {equipment.parameters.length > 0 && (
            <div className="rounded-md border divide-y">
              {equipment.parameters.map((param) => (
                <div
                  key={param.id}
                  className="flex items-center gap-3 px-3 py-1.5 group hover:bg-muted/40"
                >
                  <span className="flex-1 text-sm">{param.name}</span>
                  <span className="text-xs text-muted-foreground w-16 text-right">
                    {param.unit ?? ""}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                    onClick={() => handleRemoveParam(param.id)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {equipment.parameters.length === 0 && (
            <div className="rounded-md border border-dashed p-3 text-center text-xs text-muted-foreground">
              No parameters assigned. Add one below.
            </div>
          )}

          {availableParams.length > 0 && (
            <Combobox
              options={availableParams}
              value=""
              onValueChange={handleAddParam}
              placeholder="+ Add parameter"
              searchPlaceholder="Search parameters..."
              emptyState="No more parameters available."
              triggerClassName="h-7 text-xs border-dashed w-48"
              portal={false}
            />
          )}
        </div>
      )}
    </div>
  );
}
